import { useState, useRef } from "react";

export default function useLibrary(showToast) {
  const [likedTracks, setLikedTracks] = useState(new Map());
  const [favoritedTracks, setFavoritedTracks] = useState(new Map());
  const userRef = useRef(null);

  /** Allow App to keep the user ref up to date (called every render) */
  function syncUser(user) {
    userRef.current = user;
  }

  async function syncLibraryFromServer(token) {
    try {
      const [likedRes, favRes] = await Promise.all([
        fetch("/api/actions/liked/details", {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch("/api/favorites/list/details", {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      if (likedRes.ok) {
        const likedData = await likedRes.json();
        const likedMap = new Map();
        for (const item of likedData) {
          if (!item.track) continue; // skip entries with no valid track
          likedMap.set(item.track.id, {
            track: item.track,
            scene: item.atmosphere || item.scene || null,
          });
        }
        setLikedTracks(likedMap);
      }
      if (favRes.ok) {
        const favData = await favRes.json();
        const favMap = new Map();
        for (const item of favData) {
          if (!item.track) continue;
          favMap.set(item.track.id, {
            track: item.track,
            scene: item.atmosphere || item.scene || null,
          });
        }
        setFavoritedTracks(favMap);
      }
    } catch (e) {
      console.error("syncLibrary error:", e);
    }
  }

  async function onToggleLike(track, scene) {
    const u = userRef.current;
    const loggedIn = u !== null;
    const updated = new Map(likedTracks);
    if (updated.has(track.id)) {
      updated.delete(track.id);
      setLikedTracks(updated);
      showToast("已取消喜欢", "info");
      if (loggedIn) {
        try {
          await fetch("/api/actions/", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${u.token}`,
            },
            body: JSON.stringify({
              track_id: track.id,
              action: "unlike",
              atmosphere: scene?.atmo || scene?.id || "",
            }),
          });
        } catch (_e) { /* optimistic */ }
      }
    } else {
      if (!loggedIn) {
        showToast("请先登录后使用喜欢功能", "warning");
        return;
      }
      updated.set(track.id, {
        track,
        scene: scene?.atmo || scene?.id || null,
      });
      setLikedTracks(updated);
      showToast("已添加喜欢", "success");
      try {
        await fetch("/api/actions/", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${u.token}`,
          },
          body: JSON.stringify({
            track_id: track.id,
            action: "like",
            atmosphere: scene?.atmo || scene?.id || "",
          }),
        });
      } catch (_e) { /* optimistic */ }
    }
  }

  async function onToggleFavorite(track, scene) {
    const u = userRef.current;
    const loggedIn = u !== null;
    const updated = new Map(favoritedTracks);
    if (updated.has(track.id)) {
      updated.delete(track.id);
      setFavoritedTracks(updated);
      showToast("已取消收藏", "info");
      if (loggedIn) {
        try {
          await fetch("/api/favorites/toggle", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${u.token}`,
            },
            body: JSON.stringify({
              track_id: track.id,
              atmosphere: scene?.atmo || scene?.id || "",
            }),
          });
        } catch (_e) { /* optimistic */ }
      }
    } else {
      if (!loggedIn) {
        showToast("请先登录后使用收藏功能", "warning");
        return;
      }
      updated.set(track.id, {
        track,
        scene: scene?.atmo || scene?.id || null,
      });
      setFavoritedTracks(updated);
      showToast("已收藏", "success");
      try {
        await fetch("/api/favorites/toggle", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${u.token}`,
          },
          body: JSON.stringify({
            track_id: track.id,
            atmosphere: scene?.atmo || scene?.id || "",
          }),
        });
      } catch (_e) { /* optimistic */ }
    }
  }

  function resetLibrary() {
    setLikedTracks(new Map());
    setFavoritedTracks(new Map());
  }

  return {
    likedTracks,
    favoritedTracks,
    syncLibraryFromServer,
    onToggleLike,
    onToggleFavorite,
    resetLibrary,
    syncUser,
  };
}
